import { DEFAULT_COMPLIANCE_CATEGORIES } from './complianceCategoryTypes';
import { isMedErrorSupervisionDepartmentExcluded } from './medErrorWardMap';
import { isNeedleAndSharpDepartmentExcluded } from './needleAndSharpPolicy';
import { isSirenBundleDepartmentExcluded } from './sirenBundlePolicy';

export type VisibleComplianceCard = {
  title: string;
  categoryDocId?: string;
};

export type VisibleComplianceCardFlags = {
  hydrationCategoryKeys: readonly string[];
  isSupervisorNurse: boolean;
  isGlobalViewRole: boolean;
  departmentId: string | null;
  canViewOrgClimate: boolean;
  /** Head ward nurses who are not supervisors only keep the report-card allowlist. */
  applyHeadWardReportAllowlist?: boolean;
};

const REPORTING_CARD_IDS = new Set(['ethicSurveys', 'med_error_incident', 'fall_incident']);
const REPORTING_CARD_TITLES = new Set([
  'บรรยากาศองค์กรจริยธรรม',
  'รายงานอุบัติการณ์ความคลาดเคลื่อนทางยา',
  'จำนวนอุบัติการณ์การพลัดตกหกล้ม',
]);

const HEAD_WARD_REPORT_KEYS = [
  'nurse_notes',
  'ethicSurveys',
  'med_error',
  'siren_bundle',
  'needleandsharp',
  'บันทึกทางการพยาบาล',
  'นิเทศระบบยา',
  'SIREN bundle',
  'เข็มตำ/ของมีคมบาด',
  'บรรยากาศองค์กรจริยธรรม',
] as const;

function isReportingCard(card: VisibleComplianceCard): boolean {
  const id = card.categoryDocId ?? '';
  return REPORTING_CARD_IDS.has(id) || REPORTING_CARD_TITLES.has(card.title);
}

function hydrationKeysFor(flags: VisibleComplianceCardFlags): readonly string[] {
  if (flags.hydrationCategoryKeys.length > 0) return flags.hydrationCategoryKeys;
  if (flags.applyHeadWardReportAllowlist) return HEAD_WARD_REPORT_KEYS;
  return [];
}

export function selectVisibleComplianceCards<T extends VisibleComplianceCard>(
  cards: readonly T[],
  flags: VisibleComplianceCardFlags,
): T[] {
  const hydrationKeys = hydrationKeysFor(flags);
  const hydrated =
    hydrationKeys.length > 0
      ? cards.filter(
          (card) =>
            hydrationKeys.includes(card.title) || hydrationKeys.includes(card.categoryDocId ?? ''),
        )
      : [...cards];

  return hydrated.filter((card) => {
    const id = card.categoryDocId ?? '';
    if (id === 'orgClimateSurveys' || card.title === 'แบบประเมินบรรยากาศจริยธรรมในองค์การพยาบาล') {
      return flags.canViewOrgClimate;
    }
    if (isReportingCard(card)) {
      return flags.isSupervisorNurse || flags.isGlobalViewRole;
    }
    if ((card.title === 'นิเทศระบบยา' || id === 'med_error') && isMedErrorSupervisionDepartmentExcluded(flags.departmentId)) {
      return false;
    }
    if ((card.title === 'SIREN bundle' || id === 'siren_bundle') && isSirenBundleDepartmentExcluded(flags.departmentId)) {
      return false;
    }
    if (
      (card.title === 'เข็มตำ/ของมีคมบาด' || id === 'needleandsharp') &&
      isNeedleAndSharpDepartmentExcluded(flags.departmentId)
    ) {
      return false;
    }
    return true;
  });
}

/** Href for a form hit. Only categories the report hub already reads from the query string, plus the HR route. */
export function complianceFormSearchHref(categoryId: string, canViewOrgClimate: boolean): string | null {
  if (categoryId === 'nurse_notes' || categoryId === 'ethicSurveys') {
    return `/report?category=${categoryId}`;
  }
  if (categoryId === 'orgClimateSurveys' && canViewOrgClimate) {
    return '/report?category=orgClimateSurveys';
  }
  if (categoryId === 'hr') return '/hr';
  return null;
}

export function defaultComplianceSearchCards(): VisibleComplianceCard[] {
  return DEFAULT_COMPLIANCE_CATEGORIES.map((category) => ({
    title: category.title,
    categoryDocId: category.id,
  }));
}
